// Clean rebuilt API service file (removes previous corruption and adds 412 retry logic)
import type {
  ResolvedConfiguration,
  SecretManifestEntry,
  PresetConfiguration,
  JsonPatchOperation,
  JsonPatchResponse,
  PresetPatchResult,
  ConfigurationCacheStatistics,
  AuditEntry,
  SchemaValidationRequest,
  TemplateTestRequest,
  ApiErrorResponse,
  Tenant,
  TemplateIntrospection,
  PrecedenceLevelsResponse,
  CreateTenantRequest,
} from '@/types/configuration';
import { authService } from './authService';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

async function getDefaultHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  const baseHeaders: Record<string, string> = { Accept: 'application/json' };

  // Add bearer token if user is authenticated
  if (authService.isAuthenticated()) {
    try {
      const token = await authService.getAccessToken();
      if (token) {
        baseHeaders['Authorization'] = `Bearer ${token}`;
        console.log('[API] Added bearer token to request');
      } else {
        console.warn('[API] No token available despite authenticated status');
      }
    } catch (error) {
      console.error('[API] Failed to get access token:', error);
      // Don't throw - let the API call proceed and fail with 401 if needed
      // The user will see the error and can log in again
    }
  } else {
    console.warn('[API] User not authenticated, no bearer token added');
  }

  return { ...baseHeaders, ...extra };
}

class ApiError extends Error {
  response: ApiErrorResponse;
  status: number;
  constructor(response: ApiErrorResponse, status: number) {
    super(response.message);
    this.name = 'ApiError';
    this.response = response;
    this.status = status;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const etag = response.headers.get('ETag') || response.headers.get('etag');
  if (!response.ok) {
    if (response.status === 415)
      throw new ApiError(
        { message: 'Unsupported Media Type', details: '415' } as ApiErrorResponse,
        415
      );
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try {
        throw new ApiError((await response.json()) as ApiErrorResponse, response.status);
      } catch {
        throw new ApiError(
          {
            message: `HTTP ${response.status}: ${response.statusText}`,
            details: 'Malformed JSON error response',
          } as ApiErrorResponse,
          response.status
        );
      }
    }
    throw new ApiError(
      {
        message: `HTTP ${response.status}: ${response.statusText}`,
        details: 'No JSON error body returned',
      } as ApiErrorResponse,
      response.status
    );
  }
  if (response.status === 204) return undefined as T;
  const data = await response.json();
  if (etag && data && typeof data === 'object' && !Array.isArray(data)) (data as any).eTag = etag;
  return data;
}

export class ConfigurationService {
  async getTenants(): Promise<Tenant[]> {
    const headers = await getDefaultHeaders();
    return handleResponse(await fetch(`${API_BASE}/tenants`, { headers }));
  }

  async getResolvedConfiguration(
    tenantId: string,
    presetId?: string,
    includeInherited = true,
    forceReload = false
  ): Promise<ResolvedConfiguration> {
    const params = new URLSearchParams();
    if (presetId) params.set('presetId', presetId);
    params.set('includeInherited', includeInherited.toString());
    if (forceReload) params.set('force', 'true');
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/configuration/tenant/${tenantId}?${params}`, { headers })
    );
  }

  async getSecretManifest(tenantId: string): Promise<SecretManifestEntry[]> {
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/configuration/tenant/${tenantId}/secrets`, { headers })
    );
  }

  async getPreset(presetId: string): Promise<PresetConfiguration> {
    const headers = await getDefaultHeaders();
    return handleResponse(await fetch(`${API_BASE}/preset/${presetId}`, { headers }));
  }

  async applyPatch(
    tenantId: string,
    operations: JsonPatchOperation[],
    ifMatch?: string
  ): Promise<JsonPatchResponse> {
    const baseHeaders = { 'Content-Type': 'application/json' };
    const body = JSON.stringify(operations);
    const doReq = async (match?: string) => {
      const headers = await getDefaultHeaders(
        match ? { ...baseHeaders, 'If-Match': match } : baseHeaders
      );
      return fetch(`${API_BASE}/configuration/tenant/${tenantId}`, {
        method: 'PATCH',
        headers,
        body,
      });
    };
    let resp = await doReq(ifMatch);
    if (resp.status === 412) {
      try {
        const headers = await getDefaultHeaders();
        const latest = await fetch(
          `${API_BASE}/configuration/tenant/${tenantId}?includeInherited=true`,
          { headers }
        );
        if (latest.ok) {
          const latestEtag = latest.headers.get('ETag') || latest.headers.get('etag');
          if (latestEtag && latestEtag !== ifMatch) resp = await doReq(latestEtag);
        }
      } catch {
        /* ignore retry failure */
      }
    }
    return handleResponse(resp);
  }

  async applyPresetPatch(
    presetId: number,
    operations: JsonPatchOperation[],
    ifMatch?: string
  ): Promise<PresetPatchResult> {
    const body = JSON.stringify(operations);
    const headers = await getDefaultHeaders(
      ifMatch
        ? { 'Content-Type': 'application/json', 'If-Match': ifMatch }
        : { 'Content-Type': 'application/json' }
    );
    return handleResponse(
      await fetch(`${API_BASE}/preset/${presetId}`, { method: 'PATCH', headers, body })
    );
  }

  async clearTenantCache(tenantId: string): Promise<ResolvedConfiguration> {
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/configuration/tenant/${tenantId}?force=true`, { headers })
    );
  }

  async clearPresetCache(presetId: number): Promise<void> {
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/configuration/cache/preset/${presetId}`, {
        method: 'DELETE',
        headers,
      })
    );
  }

  async getCacheStatistics(): Promise<ConfigurationCacheStatistics> {
    return handleResponse(
      await fetch(`${API_BASE}/configuration/cache/statistics`, {
        headers: { Accept: 'application/json' },
      })
    );
  }

  async generateRollbackPatch(sessionId: string): Promise<JsonPatchOperation[]> {
    const headers = await getDefaultHeaders();
    const r = await handleResponse<{ operations: JsonPatchOperation[] }>(
      await fetch(`${API_BASE}/configuration/rollback/${sessionId}`, { headers })
    );
    return r.operations;
  }

  async getSchemaByModule(moduleName: string): Promise<any> {
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/schemavalidation/schema/${moduleName}`, { headers })
    );
  }

  async getAllSchemas(): Promise<any[]> {
    const headers = await getDefaultHeaders();
    return handleResponse(await fetch(`${API_BASE}/schemavalidation/schemas`, { headers }));
  }

  async getSchemasByModules(modules: string[]): Promise<Record<string, any>> {
    const params = new URLSearchParams();
    params.set('modules', modules.join(','));
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/schemavalidation/schemas/modules?${params}`, { headers })
    );
  }

  async getPresetSchemas(presetId: string): Promise<any> {
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/schemavalidation/preset/${presetId}/schemas`, { headers })
    );
  }

  async getTemplateIntrospection(tenantId: string, maxDepth = 10): Promise<TemplateIntrospection> {
    const params = new URLSearchParams();
    params.set('maxDepth', maxDepth.toString());
    const headers = await getDefaultHeaders();
    return handleResponse(
      await fetch(`${API_BASE}/template/introspect/${tenantId}?${params}`, { headers })
    );
  }

  async getPresetsByType(presetTypeName: string): Promise<any[]> {
    const headers = await getDefaultHeaders();
    return handleResponse(await fetch(`${API_BASE}/preset/type/${presetTypeName}`, { headers }));
  }

  async createPreset(request: {
    Name: string;
    Description?: string;
    PresetType: string;
    Json: any;
    IsActive?: boolean;
  }): Promise<any> {
    const body = {
      name: request.Name,
      description: request.Description,
      presetType: request.PresetType,
      json: request.Json,
      isActive: request.IsActive ?? true,
    };
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    return handleResponse(
      await fetch(`${API_BASE}/preset`, { method: 'POST', headers, body: JSON.stringify(body) })
    );
  }

  async getPrecedenceLevels(): Promise<PrecedenceLevelsResponse> {
    const headers = await getDefaultHeaders();
    return handleResponse(await fetch(`${API_BASE}/precedence`, { headers }));
  }

  async createTenant(request: CreateTenantRequest): Promise<Tenant> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    return handleResponse(
      await fetch(`${API_BASE}/tenants`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      })
    );
  }
}

export class AuditService {
  async getSessionAudit(sessionId: string): Promise<any> {
    return handleResponse(
      await fetch(`${API_BASE}/audit/session/${sessionId}`, {
        headers: { Accept: 'application/json' },
      })
    );
  }
  async getTenantAuditHistory(tenant: string, limit = 100): Promise<AuditEntry[]> {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    return handleResponse(
      await fetch(`${API_BASE}/audit/tenant/${tenant}?${params}`, {
        headers: { Accept: 'application/json' },
      })
    );
  }
}

export class SchemaValidationService {
  async validateConfiguration(request: SchemaValidationRequest): Promise<any> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    return handleResponse(
      await fetch(`${API_BASE}/schemavalidation/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      })
    );
  }
  async validateSchema(schema: string): Promise<any> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    return handleResponse(
      await fetch(`${API_BASE}/schemavalidation/validate-schema`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ schema }),
      })
    );
  }
  async extractVSCodeProperties(schema: string): Promise<any> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    return handleResponse(
      await fetch(`${API_BASE}/schemavalidation/extract-vscode-properties`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ schema }),
      })
    );
  }
}

export class TemplateService {
  async testTemplateSubstitution(request: TemplateTestRequest): Promise<any> {
    const headers = await getDefaultHeaders({ 'Content-Type': 'application/json' });
    return handleResponse(
      await fetch(`${API_BASE}/templateengine/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      })
    );
  }
}

export const configurationService = new ConfigurationService();
export const auditService = new AuditService();
export const schemaValidationService = new SchemaValidationService();
export const templateService = new TemplateService();
